import { useState } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { Button, MenuList, MenuItem, ClickAwayListener, Grow, Popper, useTheme } from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import _ from 'lodash';
import { useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';

import { flowchartLogger } from 'src/lib/logger';

import type { Node, ReactFlowInstance } from '@xyflow/react';

interface NodeMenuItem {
  name: string;
  label: string;
}

interface MenuDropdownProps {
  label: string;
  labelBefore?: React.ReactElement;
  labelAfter?: React.ReactElement;
  menuItems: NodeMenuItem[];
  handleMenuItemClick?: (event: React.MouseEvent<HTMLLIElement>, reactFlowInstance: ReactFlowInstance) => void;
}

const defaultHandleMenuItemClick = (
  event: React.MouseEvent<HTMLLIElement>,
  reactFlowInstance: ReactFlowInstance
): void => {
  const nodeType = event.currentTarget.getAttribute('data-node-type');
  flowchartLogger.debug('Creating node of type:', nodeType);
  if (nodeType !== null) {
    // Get current viewport to calculate center position
    const viewport = reactFlowInstance.getViewport();

    // Get the React Flow wrapper dimensions from the DOM
    const rfWrapper = document.querySelector('.react-flow__viewport')?.parentElement;
    const width = rfWrapper?.offsetWidth ?? window.innerWidth;
    const height = rfWrapper?.offsetHeight ?? window.innerHeight;

    // Calculate center of visible viewport in flow coordinates
    // Flow coordinates = (screen pixels - viewport pan) / zoom
    const centerX = (-viewport.x + width / 2) / viewport.zoom;
    const centerY = (-viewport.y + height / 2) / viewport.zoom;

    const id: string = uuidv4();
    const newNode: Node = {
      id,
      data: {},
      position: { x: centerX, y: centerY },
      type: nodeType,
      selected: false,
      dragging: false,
    };

    reactFlowInstance.setNodes((nodes) => nodes.concat(newNode));
  }
};

const MenuDropdown = ({
  label,
  labelBefore = undefined,
  labelAfter = undefined,
  menuItems,
  handleMenuItemClick = defaultHandleMenuItemClick,
}: MenuDropdownProps): React.ReactElement => {
  const translate = useTranslate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const reactFlowInstance = useReactFlow();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    flowchartLogger.debug('Menu status', event.currentTarget !== anchorEl);
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };

  function handleClose(): void {
    flowchartLogger.debug('Closing menu');
    setAnchorEl(null);
  }

  return (
    <div onMouseLeave={handleClose}>
      <Button
        variant='contained'
        id={`${_.kebabCase(label)}-button`}
        data-testid={`menu-dropdown-button`}
        aria-controls={open ? `${_.kebabCase(label)}-menu` : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleMenuOpen}
        onMouseOver={handleMenuOpen}
        sx={{
          borderTopLeftRadius: theme.shape.borderRadius,
          borderTopRightRadius: theme.shape.borderRadius,
          borderBottomLeftRadius: open ? 0 : theme.shape.borderRadius,
          borderBottomRightRadius: open ? 0 : theme.shape.borderRadius,
          borderBottom: '1px solid transparent',
          boxShadow: 'none',
          bgcolor: 'background.default',
          color: 'text.primary',
          padding: theme.spacing(2),
          width: 180,
          textTransform: 'capitalize',
          fontSize: 14,
          fontWeight: 500,
          justifyContent: 'flex-start',
          transition: 'border-radius 0.2s ease-in-out',
          gap: 1,
          '&:hover': {
            bgcolor: 'background.default',
            boxShadow: 'none',
          },
          '&[aria-controls]:not(:empty)': {
            // Styles for when the [aria-controls] attribute is not empty
            // borderRadius: `${theme.shape.borderRadius} ${theme.shape.borderRadius}px 0 0`,
          },
        }}
      >
        {labelBefore !== undefined && labelBefore}
        {translate(label)}
        {labelAfter !== undefined && labelAfter}
        <ArrowDropDownRoundedIcon
          className='menu-dropdown-arrow'
          style={{ position: 'absolute', right: theme.spacing(2) }}
        />
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        role={undefined}
        placement='bottom-start'
        transition
        disablePortal
        sx={{ zIndex: 15000, width: 180 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderTop: `1px solid ${theme.palette.text.primary}`,
              borderBottomLeftRadius: theme.shape.borderRadius,
              borderBottomRightRadius: theme.shape.borderRadius,
              paddingBottom: theme.spacing(2),
              position: 'relative',
              top: '-1px',
            }}
          >
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id={`${_.kebabCase(label)}-menu`}
                  aria-labelledby={`${_.kebabCase(label)}-button`}
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    handleMenuItemClick(event as React.MouseEvent<HTMLLIElement>, reactFlowInstance);
                    handleClose();
                  }}
                >
                  {menuItems.map((item) => (
                    <MenuItem
                      key={item.name}
                      onClick={(event) => {
                        handleMenuItemClick(event, reactFlowInstance);
                      }}
                      data-node-type={item.name}
                    >
                      {translate(item.label)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default MenuDropdown;
